"use client";

import { useEffect, useSyncExternalStore, type ComponentProps } from "react";
import { ConvexError } from "convex/values";
import { ReloadNotice } from "@/components/app-update/reload-notice";
import { ErrorPageContent } from "@/components/feedback/error-page-content";
import { Card } from "@/components/ui/card";
import { needsManualReload, type ChunkLoadRecoveryOutcome } from "@/lib/chunk-load-recovery";
import { captureError } from "@/lib/error-tracking";
import { recoverFromRetiredDeployment } from "@/lib/error-tracking/deployment-recovery";
import {
  isBrowserNetworkFailure,
  networkFailureCaptureContext,
} from "@/lib/telemetry/browser-network-failure";
import { isExpectedApplicationError } from "@/lib/telemetry/expected-errors";

interface RouteErrorViewProps {
  error: Error & { digest?: string };
  reset: () => void;
  navigation: {
    label: string;
    href: string;
  };
  title?: string;
  description?: string;
  variant?: "centered" | "card";
  showStack?: boolean;
  reportTitlePrefix?: string;
  reportable?: boolean;
  containerClassName: string;
  icon?: ComponentProps<typeof ErrorPageContent>["icon"];
  skipErrorCapture?: boolean;
}

const recoveryOutcomes = new WeakMap<Error, ChunkLoadRecoveryOutcome>();
const outcomeListeners = new Set<() => void>();

function subscribeToOutcomes(listener: () => void): () => void {
  outcomeListeners.add(listener);
  return () => {
    outcomeListeners.delete(listener);
  };
}

function recordOutcome(error: Error, outcome: ChunkLoadRecoveryOutcome): void {
  recoveryOutcomes.set(error, outcome);
  for (const listener of outcomeListeners) listener();
}

function useRouteErrorRecovery(
  error: Error & { digest?: string },
  navigation: RouteErrorViewProps["navigation"],
  skipErrorCapture: boolean,
): boolean {
  const outcome = useSyncExternalStore(
    subscribeToOutcomes,
    () => recoveryOutcomes.get(error),
    () => undefined,
  );

  useEffect(() => {
    if (recoveryOutcomes.has(error)) return;
    if (error instanceof ConvexError || isExpectedApplicationError(error)) {
      recordOutcome(error, "not_chunk_load_error");
      return;
    }
    const recovery = recoverFromRetiredDeployment(error);
    recordOutcome(error, recovery);
    if (recovery === "reloading" || skipErrorCapture) return;

    captureError(error, {
      ...networkFailureCaptureContext(error, "route-error-view"),
      tags: {
        surface: "route-error-view",
        ...(isBrowserNetworkFailure(error) && { browser_network_failure: true }),
        ...(recovery === "exhausted" && {
          chunk_load_recovery_exhausted: true,
        }),
        ...(recovery === "storage_unavailable" && {
          chunk_load_recovery_storage_unavailable: true,
        }),
      },
      extra: {
        digest: error.digest,
        navigationHref: navigation.href,
        navigationLabel: navigation.label,
      },
    });
  }, [error, navigation.href, navigation.label, skipErrorCapture]);

  return outcome !== undefined && needsManualReload(outcome);
}

function StaleBuildPage({
  navigation,
  variant,
}: {
  navigation: RouteErrorViewProps["navigation"];
  variant: "centered" | "card";
}) {
  const notice = <ReloadNotice size="page" navigation={navigation} />;
  if (variant === "card") {
    return (
      <Card padding="roomy" className="w-full max-w-sm">
        {notice}
      </Card>
    );
  }
  return <div className="w-full max-w-sm">{notice}</div>;
}

export function RouteErrorView({
  error,
  reset,
  navigation,
  title,
  description,
  variant = "centered",
  showStack = true,
  reportTitlePrefix,
  reportable,
  containerClassName,
  icon,
  skipErrorCapture = false,
}: RouteErrorViewProps) {
  const staleBuild = useRouteErrorRecovery(error, navigation, skipErrorCapture);

  return (
    <div className={containerClassName}>
      {staleBuild ? (
        <StaleBuildPage navigation={navigation} variant={variant} />
      ) : (
        <ErrorPageContent
          error={error}
          reset={reset}
          title={title}
          description={description}
          icon={icon}
          variant={variant}
          showStack={showStack}
          navigation={navigation}
          reportTitlePrefix={reportTitlePrefix}
          reportable={reportable}
        />
      )}
    </div>
  );
}
